import { Stage, Layer, Line, Image, Text } from 'react-konva'
import React, { useEffect, useRef, useState } from 'react'
import { Button, Box } from '@mui/material'
import EraserIcon from '@mui/icons-material/EditOff'
import useImage from 'use-image'

type SignaturePadProps = {
	formText: string
	formLogo: string
	onSign: (dataUrl: string, lines: Array<{ points: number[] }>) => void
    date?: string
    initialLines?: Array<{ points: number[] }>
    onClear?: () => void
}

type Line = {
    points: number[]
}

const SignaturePad = ({ formText, formLogo, onSign, date, initialLines, onClear }: SignaturePadProps) => {
    const isDrawing = useRef(false)
    const stageRef = useRef<any>(null)
    const [lines, setLines] = useState<Array<{ points: number[] }>>(initialLines || [])
    const [logoImage] = useImage(formLogo)

    useEffect(() => {
        if (lines.length > 0) {
            const dataUrl = stageRef.current.toDataURL({
                mimeType: 'image/png',
            })
            onSign(dataUrl, lines)
        }
    }, [lines])

    const handleMouseDown = () => {
        isDrawing.current = true
        const stage = stageRef.current
        const point = stage.getPointerPosition()
        setLines([...lines, { points: [point.x, point.y] }])
    }

    const handleMouseMove = () => {
        if (!isDrawing.current) return

        const stage = stageRef.current
        const point = stage.getPointerPosition()
        const lastLine = lines[lines.length - 1]
        
        // Add point to the last line
        lastLine.points = lastLine.points.concat([point.x, point.y])
        
        // Force update
        setLines([...lines])
    }

    const handleMouseUp = () => {
        isDrawing.current = false
    }

    const handleClear = () => {
        setLines([])
        onClear?.()
    }

    return (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Stage
                width={800}
                height={420}
                onMouseDown={handleMouseDown}
                onMousemove={handleMouseMove}
                onMouseup={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchMove={handleMouseMove}
                onTouchEnd={handleMouseUp}
                ref={stageRef}
                style={{ border: '1px solid black' }}
            >
                <Layer>
                    {logoImage && <Image 
                        image={logoImage} 
                        y={20}
                        x={20}
                        width={200}
                        height={logoImage.height * (200 / logoImage.width)}
                    />}
                    <Text 
                        text={formText} 
                        y={80}
                        x={20}
                        fontSize={16}
                        fontFamily="Times New Roman"
                        lineHeight={1.5}
                        width={750}
                    />
                    {date && <Text
                        text={date}
                        y={400}
                        x={700}
                        fontSize={16}
                        fontFamily="Times New Roman"
                    />}
                    {date && <Text
                        text={"Name & Signature"}
                        y={400}
                        x={50}
                        fontSize={16}
                        fontFamily="Times New Roman"
                    />}
                    {lines.map((line, i) => (
                        <Line
                            key={i}
                            points={line.points}
                            stroke='black'
                            strokeWidth={2}
                            tension={0.5}
                            lineCap='round'
                            globalCompositeOperation='source-over'
                        />
                    ))}
                </Layer>
            </Stage>
            <Button 
                variant="outlined" 
                color="warning"
                onClick={handleClear}
                startIcon={<EraserIcon />}
                sx={{ 
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    minWidth: 50,
                    height: 40,
                    textTransform: 'none',
                    fontWeight: 'medium',
                    zIndex: 1,
                    color: 'warning.main',
                    borderColor: 'warning.main',
                    '&:hover': {
                        backgroundColor: 'warning.light',
                        borderColor: 'warning.dark',
                    }
                }}
            >
                Clear
            </Button>
        </Box>
    )
}

export default SignaturePad
